import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { clsx } from 'clsx';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  className?: string;
  items?: BreadcrumbItem[];
}

const routeLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  categories: 'Categories',
  procurement: 'Procurement',
  suppliers: 'Suppliers',
  invoices: 'Purchase Invoices',
  'cost-analytics': 'Cost Analytics',
  items: 'Items & Recipes',
  imports: 'Sales Import',
  consumption: 'Consumption',
  integrations: 'Integrations',
  privacy: 'GDPR & Privacy',
  settings: 'Settings',
  'pos-warehouse': 'POS & Warehouses',
  'transfer-requests': 'Transfer Requests',
  'warehouse-analytics': 'Warehouse Analytics',
  'menu-import': 'AI Menu Import',
  'recipe-review': 'Recipe Review',
  'predictive-inventory': 'Predictive Inventory',
  'waste-reduction': 'Waste Reduction',
  'supplier-contracts': 'Supplier Contracts',
  receiving: 'Receiving Queue',
  branding: 'White Label Branding',
  pricing: 'Pricing',
  onboarding: 'Onboarding',
  usage: 'Usage Statistics',
  'faq-management': 'FAQ Management',
  faq: 'Help Center',
  'mydata-sync': 'MyData Sync',
  'channel-optimizer': 'Channel Optimizer',
  'delivery-notes': 'Delivery Notes',
  'super-admin': 'Super Admin Portal',
};

export function Breadcrumb({ className, items }: BreadcrumbProps) {
  const location = useLocation();

  let breadcrumbs: BreadcrumbItem[];

  if (items) {
    breadcrumbs = items;
  } else {
    const pathSegments = location.pathname.split('/').filter(Boolean);

    breadcrumbs = [
      { label: 'Home', href: '/dashboard' },
    ];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathSegments.length - 1;

      breadcrumbs.push({
        label: routeLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1),
        href: isLast ? undefined : currentPath,
      });
    });
  }

  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav className={clsx('flex items-center space-x-2 text-sm', className)}>
      {breadcrumbs.map((crumb, index) => (
        <div key={index} className="flex items-center">
          {index > 0 && (
            <ChevronRight className="w-4 h-4 text-white/40 mx-2" />
          )}
          {crumb.href ? (
            <Link
              to={crumb.href}
              className="flex items-center text-white/60 hover:text-white transition-colors"
            >
              {index === 0 && <Home className="w-4 h-4 mr-1" />}
              {crumb.label}
            </Link>
          ) : (
            <span className="text-white font-medium flex items-center">
              {index === 0 && <Home className="w-4 h-4 mr-1" />}
              {crumb.label}
            </span>
          )}
        </div>
      ))}
    </nav>
  );
}
